import React, { Component } from 'react';

import Cell from './Cell';
import Scoring from '../Scoring';

import '../styles/Grid.css';

class Grid extends Component {
  isStartLetter = (row, column) => {
    return this.props.startLetters && Object.keys(this.props.startLetters).includes("" + row + "," + column);
  }

  // Whether a line still has an empty cell to push into. A full line cannot be
  // slid at all, and loses its arrows outright -- which is most of the point of
  // putting them on screen: the moves available are the moves visible.
  hasGap = (edge) => {
    const rows = this.props.letters.length;
    const columns = this.props.letters[0].length;

    return Scoring.getLineCoordinates(rows, columns, edge)
      .some(({ row, column }) => this.props.letters[row][column].length === 0);
  }

  renderArrow = (edge, glyph) => {
    // Thin across the edge it sits on, and exactly as long as a cell along it,
    // so an arrow lines up with the line it pushes.
    const orientation = edge.line === "row" ? "grid-arrow-across" : "grid-arrow-down";
    const key = `${edge.line}_${edge.index}_${edge.from}`;

    // A line with no gap cannot be pushed, so it has no arrow: not a faded one,
    // and nothing to hover or describe. Its space is kept all the same -- the
    // board would shift under the player otherwise, every time a line filled.
    if (!this.hasGap(edge)) {
      return <div key={key} className={orientation} />;
    }

    const side = edge.from === "start"
      ? (edge.line === "row" ? "left" : "top")
      : (edge.line === "row" ? "right" : "bottom");

    return (
      <button
        key={key}
        className={"grid-arrow " + orientation}
        disabled={this.props.loading}
        title={`Push ${edge.line} ${edge.index + 1} from the ${side}`}
        onClick={() => this.props.onSlide(edge)}
      >
        {glyph}
      </button>
    );
  }

  // The arrows above or below the board, with a blank square at each end so the
  // ones down the sides have somewhere to sit.
  renderColumnArrows = (from, glyph) => {
    const arrows = [<div key="corner-start" className="grid-arrow-corner" />];

    for (let j = 0; j < this.props.letters[0].length; j++) {
      arrows.push(this.renderArrow({ line: "column", index: j, from }, glyph));
    }
    arrows.push(<div key="corner-end" className="grid-arrow-corner" />);

    return <div className="grid-arrow-row">{arrows}</div>;
  }

  renderRowArrows = (from, glyph) => {
    const arrows = [];

    for (let i = 0; i < this.props.letters.length; i++) {
      arrows.push(this.renderArrow({ line: "row", index: i, from }, glyph));
    }

    return <div className="grid-arrow-column">{arrows}</div>;
  }

  renderGrid = () => {
    let grid = [];

    for (let i=0; i<this.props.letters.length; i++) {
      let row = [];

      for (let j=0; j<this.props.letters[0].length; j++) {
        // Measured around the line, so a word that wraps off one edge is drawn
        // open at both: a start running off the far side, an end coming in
        // from the near one.
        const rowWord = this.props.rowData[i];
        const rowWordPosition = Scoring.getWordPosition(
          j, rowWord.wordStart, rowWord.wordLength, this.props.letters[0].length);

        const columnWord = this.props.columnData[j];
        const columnWordPosition = Scoring.getWordPosition(
          i, columnWord.wordStart, columnWord.wordLength, this.props.letters.length);

        row.push(
          <Cell
            key={`row_${i}_col_${j}`}
            row={i}
            column={j}
            rowWordPosition={rowWordPosition}
            columnWordPosition={columnWordPosition}
            letters={this.props.letters[i][j]}
            startLetters={this.isStartLetter(i, j)}
            expanded={this.props.expansions.includes("" + i + "," + j)}
            bonus={Scoring.isBonus(this.props.bonusCells, i, j)}
            loading={this.props.loading}
            slideMode={this.props.slideMode}
            onCellSelection={this.props.onCellSelection}
          />);
      }

      grid.push(
        <div key={`row_${i}`} className="grid-row">{row}</div>
      );
    }

    return grid;
  }

  render() {
    const grid = <div className="grid">{this.renderGrid()}</div>;

    if (!this.props.slideMode) {
      return grid;
    }

    // The arrows sit outside the board rather than inside it, so `.grid` is the
    // same box it is in every other mode -- its border is what makes the outer
    // edge read as thick as the doubled borders between cells, and a board that
    // lost it would look wrong in a way that has nothing to do with sliding.
    return (
      <div className="grid-slide">
        {this.renderColumnArrows("start", "▼")}
        <div className="grid-slide-middle">
          {this.renderRowArrows("start", "▶")}
          {grid}
          {this.renderRowArrows("end", "◀")}
        </div>
        {this.renderColumnArrows("end", "▲")}
      </div>
    );
  }
}

export default Grid;
